import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { registerDataValidationErrorMatcher } from 'errors/dataValidationError';
import useKeyDownListener from 'hooks/useKeyDownListener';
import Input from 'layout/atoms/Input/Input';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import FormField from 'layout/molecules/FormField/FormField';
import FormSaveActions from 'layout/molecules/FormSaveActions/FormSaveActions';
import { closeModal, selectModalByName } from 'store/layoutSlice';
import { addModelColor, editModelColor, fetchSpecificationProductionQuantities, selectProductionQuantitiesStatus, } from 'store/specificationProductionQuantitiesSlice';
import useColorValidation from './useColorValidation';
function ColorCreator({ edit }) {
    var _a, _b;
    const { t } = useTranslation(['specification', 'common']);
    const dispatch = useDispatch();
    const status = useSelector(selectProductionQuantitiesStatus);
    const modelSpecificationId = useSelector((state) => state.specificationDetails.item.id);
    const colorDetails = (_a = useSelector(selectModalByName('colorEditor'))) === null || _a === void 0 ? void 0 : _a.data;
    const [form, setForm] = useState({
        specificationId: modelSpecificationId,
        colorName: edit && (colorDetails === null || colorDetails === void 0 ? void 0 : colorDetails.name) ? colorDetails === null || colorDetails === void 0 ? void 0 : colorDetails.name : '',
    });
    const [serverError, setServerError] = useState(null);
    const { errors, isValid, pendingFields, markTouched, validateAll } = useColorValidation(form, t);
    const handleFormChange = ({ target: { name, value } }) => {
        setForm((f) => (Object.assign(Object.assign({}, f), { [name]: value })));
        setServerError(null);
    };
    const handleCancel = () => {
        dispatch(closeModal({ name: edit ? 'colorEditor' : 'colorCreator' }));
    };
    const handleFormSubmit = () => {
        if (!validateAll())
            return;
        setServerError(null);
        if (edit) {
            dispatch(editModelColor(Object.assign(Object.assign({}, form), { colorId: (colorDetails === null || colorDetails === void 0 ? void 0 : colorDetails.id) || '' })))
                .unwrap()
                .then(() => dispatch(closeModal({ name: 'colorEditor' })))
                .catch((errorMessage) => setServerError(errorMessage));
        }
        else {
            dispatch(addModelColor(form))
                .unwrap()
                .then(() => dispatch(fetchSpecificationProductionQuantities({ specificationId: modelSpecificationId })))
                .then(() => dispatch(closeModal({ name: 'colorCreator' })))
                .catch((errorMessage) => setServerError(errorMessage));
        }
    };
    registerDataValidationErrorMatcher();
    useKeyDownListener([
        { code: 'Enter', callback: handleFormSubmit },
        { code: 'Escape', callback: handleCancel },
    ]);
    return (React.createElement(Modal, { name: edit ? 'colorEditor' : 'colorCreator', size: "s", header: React.createElement(Label, { weight: "strong" }, edit ? t('edit_color') : t('add_color')), footer: React.createElement(FormSaveActions, { isValid: isValid, isLoading: status.addColor === 'loading' || status.editColor === 'loading', pendingFields: pendingFields, onSave: handleFormSubmit, onCancel: handleCancel, validateAll: validateAll }) },
        React.createElement(FormField, { label: t('color_name'), error: (_b = errors.colorName) !== null && _b !== void 0 ? _b : serverError },
            React.createElement(Input, { value: form.colorName, onChange: handleFormChange, onBlur: () => markTouched('colorName'), name: "colorName", error: Boolean(errors.colorName) || Boolean(serverError), autoFocus: true }))));
}
export default memo(ColorCreator);
